<template>
  <div name="challengepupils" class="inner">
    <innertopbar @close="closeWindow()">
      <template v-slot:breadcrumb>
        <router-link to="/health/challenges"> Challenges </router-link>
        <i class="carret fas fa-caret-right"></i> {{ title }}
      </template>

      <template v-slot:listitems>

        <li class="inline-block">
          <button
            @click="showScoreboard()"
            type="button"
            class="filter-bar-button hover:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i class="fas fa-list-ol"></i> Scoreboard
          </button>
        </li>
        <li class="inline-block">
          <button
            @click="takeChallenge()"
            type="button"
            class="publish-button hover:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isDisabled"
          >
            Start Challenge
          </button>

   
        </li>
      </template>
    </innertopbar>

    <div class="w-full mx-auto grid grid grid-flow-col grid-cols-2 gap-10">
      <div class="mt-6 ml-8 p-6 bg-white rounded-lg mb-10">
        <infoScreen
          :challenge="challenge"
          @start="startChallenge()"
        ></infoScreen>
      </div>

      <div class="p-6 pt-10 bg-white h-screen">
        <FilterBar @searchChanged="setSearchValue">
          <template v-slot:filter>
            <div>
              <ClassDropdown
                :classSelected="filterValue.group"
                @classValueChanged="updateFilteredClass"
              ></ClassDropdown>
            </div>
            <div>
              <button
                type="button"
                class="filter-bar-button focus:outline-none hover:opacity-75"
                v-bind:class="
                  filterHasValues ? 'filter-item-button-on' : 'bg-white'
                "
                @click="showFilter()"
              >
                <i class="fas fa-filter"></i> Filters
              </button>
            </div>
          </template>

          <template v-slot:actions> </template>
        </FilterBar>

        <PupilFilterModal
          class=""
          v-if="filterIsVisible"
          :values="filterValue"
          @apply-filter="applyFilter"
          @close="showFilter"
        >
        </PupilFilterModal>

        <p v-if="showMessage" class="save-notice ml-6 mb-6 font-bold">
          {{ msg }} <i class="far fa-check-circle"></i>
        </p>

        <div class="h-screen overflow-y-auto">
          <table
            class="w-95p mx-auto mr-8 mb-2 h-10 pt-2 align-text-bottom bg-white rounded text-left text-gray-400"
          >
            <thead class="bg-gray-100 w-full">
              <tr>
                <th class="pl-2 w-4/12">
                  Pupil Name
                  <i
                    class="fas fa-sort tbh-sort"
                    v-bind:class="
                      'name' === sortBy ? 'text-blue-500' : 'text-gray-400'
                    "
                  ></i>
                </th>
                <th class="flex right">
                  <h5 class="mr-12 pt-2 font-semibold">
                    {{ pupilsSelected.length }} pupils selected
                  </h5>
                  <a
                    @click="selectAll"
                    class="float-right pt-2 cursor-pointer text-purple-700 font-bold mr-6"
                    >{{ isAllSelected ? "Deselect" : "Select" }} All</a
                  >
                </th>
              </tr>
            </thead>
          </table>

          <div class="table-wrapper">
            <div v-if="errored" class="empty ml-10 mt-4">
              <p>
                We are not able to retrieve this information at the moment,
                please try back later
              </p>
            </div>

            <div id="content-area" v-else>
              <div
                style="width: 100%; text-align: center"
                class="spin-holder mt-4"
                v-if="loading"
              >
                <i class="spinner fa fa-spinner fa-spin"></i>
                <p>Loading Pupils</p>
              </div>

              <div v-else>
                <table
                  v-if="componentLoaded"
                  class="table-wrapper mx-auto bg-white w-95p rounded shadow-sm"
                >
                  <tbody>
                    <tr
                      v-for="pupil in filteredList"
                      :key="pupil.identifier"
                      @click="selectPupil(pupil)"
                    >
                      <td class="w-4/12">{{ pupil.name }}</td>
                      <td
                        class="right text-xs mr-4 cursor-pointer hover:opacity-50"
                      >
                        <div
                          class="mr-6 top-0 mt-1 text-white p-1 rounded-full h-6 w-6"
                          v-bind:class="selectedPupilStyle(pupil)"
                        >
                          <i class="fas fa-check text-sm"></i>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <p class="m-5 mx-12" v-else>
                  We could not find any data for your given selection.
                </p>
              </div>
            </div>

            <div class="w-full mx-auto">
              <Pagination
                v-if="componentLoaded"
                :total-pages="totalPages"
                :total="totalResultsCount"
                :current-page="currentPage"
                :has-more-pages="hasMorePages"
                :pageSize="pageSize"
                @pagechanged="showMore"
                class="mt-4 mb-12"
              >
              </Pagination>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ModalSlide
      :show="showBoard"
      :headCss="'bg-opacity-25'"
      @closeModal="cancel()"
    >
      <template v-slot:header-icon>
        <i class="fas fa-bell"></i>
      </template>
      <template v-slot:header-title>
        <h2 class="text-xl font-bold">{{ challenge.title }}</h2>
      </template>
      <template v-slot:body>
        <ChallengeView
          v-if="selectedViewType != undefined"
          :viewType="selectedViewType"
          :pupils="pupilsSelected"
        ></ChallengeView>
      </template>
    </ModalSlide>
  </div>
</template>

<script>
import moment from "moment";

import { mapGetters } from "vuex";
const infoScreen = () => import("@/components/challenges/TheInfoModal.vue");
const innertopbar = () => import("@/components/InnerTopBar.vue");

const ClassDropdown = () => import("@/components/pupil/ClassDropdown.vue");
const Pagination = () => import("@/components/helpers/Pagination.vue");
const PupilFilterModal = () => import("@/components/helpers/PupilFilter.vue");
const FilterBar = () => import("@/components/FilterBarNavigation.vue");
const ModalSlide = () => import("@/components/ModalSlide.vue");

const ChallengeView = () =>
  import("@/components/challenges/TheChallengeView.vue");

import { ChallengeService } from "@/services/challenge.service";
import { PupilService } from "@/services/pupil.service";

const dropdown = () => import("@/components/Dropdown.vue");

export default {
  name: "challengepupils",
  props: {},
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
    }),
    canTakeChallenge() {
      return this.pupilsSelected.length > 0;
    },
    hasMorePages() {
      return this.pagination.has_more === true;
    },
    totalResultsCount() {
      return this.pagination.total_count;
    },
    totalPages() {
      return this.pagination.total_pages;
    },
    componentLoaded() {
      return this.filteredList.length > 0;
    },
    showMessage() {
      return this.msg != undefined && this.msg.length > 0;
    },
    searchValue() {
      return this.search;
    },
    filteredList() {
      let vm = this;
      let data = vm.pupils;
      return _.orderBy(data, [vm.sortBy, "pupil.name"], [vm.sortDirection]);
    },
    filterHasValues() {
      var value = this.filterValue;
      if (
        value.group != undefined ||
        value.years != undefined ||
        value.types != undefined ||
        (value.tags != undefined && value.length > 2)
      ) {
        return true;
      }
    },
    classSelectedValue() {
      return this.filterValue.group;
    },
    title() {
      return this.challenge.title;
    },
    isDisabled() {
      return this.pupilsSelected.length === 0;
    },
  },
  data() {
    return {
      filterIsVisible: false,
      pupils: [],

      loading: true,
      errored: false,
      page: 1,
      pageSize: 48,
      currentPage: 1,
      pagination: {},

      school: this.$store.getters.school,
      challenge: this.$store.getters.challenge,

      pupilsSelected: [],
      classes: [],
      selectedClass: {},
      search: "",
      isAllSelected: false,

      search: "",
      filterValue: {},

      sortBy: "pupil.name",
      sortDirection: "asc",

      showBoard: false,
      selectedViewType: undefined,
    };
  },
  created() {},
  mounted() {
    this.searchPupils();
  },
  methods: {
    searchPupils() {
      this.loading = true;

      PupilService.getWithFilter(this.filterValue, this.page, this.pageSize)
        .then((response) => this.buildPupils(response.data))
        .catch((err) => {
          console.log("pupils " + err);
          this.errored = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildPupils(data) {
      this.pupils = data.data;
      this.pagination = data.meta.pagination;
    },
    showFilter() {
      this.filterIsVisible = !this.filterIsVisible;
    },
    updateFilteredClass(value) {
      if (value != undefined) {
        this.filterValue.group = value;
      } else {
        this.filterValue.group = {};
      }

      this.showMore({ page: 1, size: this.pageSize });
    },
    applyFilter(filters) {
      this.filterValue = filters;
      this.updateFilteredClass(filters.group);

      this.showFilter();
      this.showMore({ page: 1, size: this.pageSize });
    },
    setSearchValue(value, time = 300) {
      this.search = value;
      this.filterValue.title = this.search;

      let vm = this;
      if (vm.search.length === 0 || vm.search.length >= 1) {
        clearTimeout(vm.timeout);
        vm.timeout = setTimeout(() => {
          vm.showMore({ page: 1, size: this.pageSize });
        }, time);
      }
    },
    showMore({ page, size }) {
      this.page = page;
      this.currentPage = page;
      this.pageSize = size;
      this.searchPupils();
      window.scrollTo(0, 0);
    },
    sort(s) {
      if (s === this.sortBy) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      }
      this.sortBy = s;
    },
    closeWindow() {
      this.$router.push({ path: "/health/challenges" });
      window.scrollTo(0, 0);
    },
    selectedPupilStyle(pupil) {
      return this.isSelected(pupil) ? "bg-green-500" : "bg-gray-300";
    },
    isSelected(value) {
      let found = this.pupilsSelected.find(
        (p) => p.identifier === value.identifier
      );
      return found;
    },
    selectPupil(pupil) {
      if (this.pupilsSelected.some((p) => p.identifier === pupil.identifier)) {
        this.pupilsSelected.splice(
          this.pupilsSelected.findIndex(
            (item) => item.identifier === pupil.identifier
          ),
          1
        );
      } else {
        this.pupilsSelected.push(pupil);
      }
    },
    selectAll() {
      this.isAllSelected = !this.isAllSelected;

      if (!this.isAllSelected) {
        this.pupilsSelected = [];
      } else {
        this.pupilsSelected = this.filteredList.map((p) => p);
      }
    },
    takeChallenge() {
      this.selectedViewType = "event";
      this.showBoard = true;
    },
    showScoreboard() {
      this.selectedViewType = "scoreboard";
      this.showBoard = true;
    },
    cancel() {
      this.pupilsSelected = [];
      this.showBoard = false;
      this.selectedViewType = undefined
    },
  },
  components: {
    dropdown,
    infoScreen,
    PupilFilterModal,
    FilterBar,
    Pagination,
    ClassDropdown,
    innertopbar,
    ModalSlide,
    ChallengeView,
  },
};
</script>

<style scoped>
.button-purple:disabled {
  @apply bg-gray-500 cursor-not-allowed;
}
</style>